import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Layout/Header';
import Modal from '../components/UI/Modal';
import StatusPill from '../components/UI/StatusPill';
import { money } from '../utils/format';
import { hasPermission, canViewCosts, protectedMoney } from '../utils/permissions';
import ProductDetail from '../components/UI/ProductDetail';
import { Plus, Eye, Send, PackageCheck, X, Copy, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [locFilter, setLocFilter] = useState('');
  const [supFilter, setSupFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [receiveItems, setReceiveItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ location_id: '', supplier_id: '', notes: '' });
  const [products, setProducts] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [sortCol, setSortCol] = useState(null);
  const [viewProductId, setViewProductId] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [searchParams, setSearchParams] = useSearchParams();
  const [initialized, setInitialized] = useState(false);
  const routeLocation = useLocation();

  // Read URL params and set state before first load
  useEffect(() => {
    const urlStatus = searchParams.get('status');
    const urlSupplier = searchParams.get('supplier_id');
    if (urlSupplier) setSupFilter(urlSupplier);
    if (urlStatus) setStatusFilter(urlStatus);

    const initData = async () => {
      const [locRes, supRes, prodRes] = await Promise.all([
        api('/api/locations').catch(() => ({ data: [] })),
        api('/api/suppliers').catch(() => ({ data: [] })),
        api('/api/products').catch(() => ({ data: [] })),
      ]);
      setLocations(locRes.data || []);
      setSuppliers(supRes.data || []);

      // Handle prefill from Operations page quick order
      const prefill = routeLocation.state?.prefill;
      if (prefill) {
        setProducts(prodRes.data || []);
        setCreateForm({
          location_id: String(prefill.location_id || ''),
          supplier_id: String(prefill.supplier_id || ''),
          notes: prefill.notes || ''
        });
        setLineItems((prefill.items || []).map(item => ({
          product_id: String(item.product_id || ''),
          qty_ordered: item.qty_ordered || 1,
          unit_cost: item.unit_cost || 0
        })));
        setShowCreate(true);
        // Clear the state so refresh doesn't re-trigger
        window.history.replaceState({}, document.title);
      } else if (searchParams.get('create') === 'true') {
        setProducts(prodRes.data || []);
        openCreate();
        setSearchParams({}, { replace: true });
      }

      setTimeout(() => setInitialized(true), 0);
    };
    initData();
  }, []);

  const load = () => {
    const p = new URLSearchParams();
    if (statusFilter) p.set('status', statusFilter);
    if (locFilter) p.set('location_id', locFilter);
    if (supFilter) p.set('supplier_id', supFilter);
    api('/api/orders?' + p).then(r => setOrders(r.data)).catch(console.error);
  };

  // Only load after initialization so URL params are applied first
  useEffect(() => { if (initialized) load(); }, [initialized, statusFilter, locFilter, supFilter]);

  // Sorting
  const toggleSort = (col) => { if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col); setSortDir('asc'); } };
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown size={12} style={{ opacity: .3, marginLeft: 4 }} />;
    return sortDir === 'asc' ? <ChevronUp size={12} style={{ color: 'var(--gold)', marginLeft: 4 }} /> : <ChevronDown size={12} style={{ color: 'var(--gold)', marginLeft: 4 }} />;
  };

  const filtered = useMemo(() => {
    let list = [...orders];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.po_number || '').toLowerCase().includes(q) ||
        (o.location_name || '').toLowerCase().includes(q) ||
        (o.supplier_name || '').toLowerCase().includes(q)
      );
    }
    if (sortCol) {
      list.sort((a, b) => {
        switch (sortCol) {
          case 'po': { const c = (a.po_number||'').localeCompare(b.po_number||''); return sortDir === 'asc' ? c : -c; }
          case 'location': { const c = (a.location_name||'').localeCompare(b.location_name||''); return sortDir === 'asc' ? c : -c; }
          case 'supplier': { const c = (a.supplier_name||'').localeCompare(b.supplier_name||''); return sortDir === 'asc' ? c : -c; }
          case 'status': { const c = (a.status||'').localeCompare(b.status||''); return sortDir === 'asc' ? c : -c; }
          case 'total': return sortDir === 'asc' ? Number(a.total_amount)-Number(b.total_amount) : Number(b.total_amount)-Number(a.total_amount);
          case 'created': return sortDir === 'asc' ? new Date(a.created_at)-new Date(b.created_at) : new Date(b.created_at)-new Date(a.created_at);
          default: return 0;
        }
      });
    }
    return list;
  }, [orders, search, sortCol, sortDir]);

  const viewOrder = async (id) => {
    const r = await api('/api/orders/' + id);
    setDetail(r.data);
    setReceiveItems((r.data.items || []).map(i => ({ ...i, qty_to_receive: Number(i.qty_ordered) })));
  };
  const sendOrder = async (id) => { await api('/api/orders/' + id + '/send', { method: 'PUT' }); load(); setDetail(null); };
  const receiveOrder = async (id) => {
    const items = receiveItems.map(i => ({ id: i.id, qty_received: i.qty_to_receive }));
    await api('/api/orders/' + id + '/receive', { method: 'PUT', body: JSON.stringify({ items }) });
    load(); setDetail(null);
  };
  const cancelOrder = async (id) => {
    if (!confirm('Cancel this purchase order? This action cannot be undone.')) return;
    await api('/api/orders/' + id, { method: 'DELETE' }); load(); setDetail(null);
  };
  const approvePO = async (id) => {
    try {
      await api('/api/orders/' + id + '/approve', { method: 'PUT' });
      load();
      setDetail(null);
    } catch (err) { alert(err.error || 'Failed to approve'); }
  };
  const rejectPO = async (id) => {
    if (!confirm('Reject this PO? It will be cancelled.')) return;
    try {
      await api('/api/orders/' + id + '/reject', { method: 'PUT' });
      load();
      setDetail(null);
    } catch (err) { alert(err.error || 'Failed to reject'); }
  };
  const openCreate = async () => {
    const r = await api('/api/products'); setProducts(r.data);
    setCreateForm({ location_id: '', supplier_id: '', notes: '' }); setLineItems([]); setShowCreate(true);
  };
  const addLineItem = () => setLineItems([...lineItems, { product_id: '', qty_ordered: 1, unit_cost: 0 }]);
  const submitCreate = async (andSend) => {
    try {
      const items = lineItems.filter(li => li.product_id).map(li => ({ product_id: parseInt(li.product_id), qty_ordered: parseFloat(li.qty_ordered), unit_cost: parseFloat(li.unit_cost) }));
      const r = await api('/api/orders', { method: 'POST', body: JSON.stringify({ ...createForm, location_id: parseInt(createForm.location_id), supplier_id: parseInt(createForm.supplier_id), items }) });
      if (andSend && r.data && r.data.id) {
        const sendRes = await api('/api/orders/' + r.data.id + '/send', { method: 'PUT' });
        alert(sendRes.message || 'PO created and sent to supplier');
      }
      setShowCreate(false); load();
    } catch (err) { alert(err.error || 'Failed'); }
  };
  const copyPortalLink = (token) => { if (!token) return; navigator.clipboard.writeText(window.location.origin + '/portal/' + token); alert('Portal link copied'); };

  const canEdit = hasPermission(user, 'send_po') || hasPermission(user, 'cancel_po');
  const canCreate = hasPermission(user, 'create_po_direct') || true; // anyone can create, but staff POs need approval
  const canApprove = hasPermission(user, 'approve_po');
  const canSend = hasPermission(user, 'send_po');
  const canReceive = hasPermission(user, 'receive_po');
  const canCancel = hasPermission(user, 'cancel_po');
  const showCosts = canViewCosts(user);
  const statuses = ['', 'draft', 'sent', 'confirmed', 'received', 'cancelled'];
  const thSort = (label, col) => (
    <th onClick={() => toggleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{label}<SortIcon col={col} /></span>
    </th>
  );

  return (
    <div>
      <Header title="Purchase Orders">
        {canCreate && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> New Order</button>}
      </Header>

      <div className="tabs">
        {statuses.map(s => (
          <button key={s} className={'tab' + (statusFilter === s ? ' active' : '')} onClick={() => setStatusFilter(s)}>
            {s === 'sent' ? 'Issued' : s || 'All'}
          </button>
        ))}
      </div>


      {statusFilter && (
        <div style={{ padding: '12px 18px', marginBottom: 16, borderRadius: 10, fontSize: 13, lineHeight: 1.6, display: 'flex', alignItems: 'flex-start', gap: 10,
          background: statusFilter === 'draft' ? 'rgba(74,106,138,.1)' : statusFilter === 'sent' ? 'rgba(74,158,191,.1)' : statusFilter === 'confirmed' ? 'rgba(120,168,232,.1)' : statusFilter === 'received' ? 'rgba(46,204,113,.1)' : statusFilter === 'cancelled' ? 'rgba(231,76,60,.1)' : 'var(--card)',
          border: '1px solid ' + (statusFilter === 'draft' ? 'rgba(74,106,138,.2)' : statusFilter === 'sent' ? 'rgba(74,158,191,.2)' : statusFilter === 'confirmed' ? 'rgba(120,168,232,.2)' : statusFilter === 'received' ? 'rgba(46,204,113,.2)' : statusFilter === 'cancelled' ? 'rgba(231,76,60,.2)' : 'var(--border)'),
          color: statusFilter === 'draft' ? '#8BA4BC' : statusFilter === 'sent' ? '#4A9EBF' : statusFilter === 'confirmed' ? '#78A8E8' : statusFilter === 'received' ? '#2ECC71' : statusFilter === 'cancelled' ? '#E74C3C' : 'var(--muted)',
        }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>
            {statusFilter === 'draft' && '📝'}
            {statusFilter === 'sent' && '📤'}
            {statusFilter === 'confirmed' && '✅'}
            {statusFilter === 'received' && '📦'}
            {statusFilter === 'cancelled' && '🚫'}
          </span>
          <span>
            {statusFilter === 'draft' && 'Draft orders have not been sent to the supplier yet. Review and send when ready.'}
            {statusFilter === 'sent' && 'Issued orders have been sent to the supplier but not yet confirmed. Follow up if no response.'}
            {statusFilter === 'confirmed' && 'Confirmed orders have been acknowledged by the supplier and are being prepared for delivery.'}
            {statusFilter === 'received' && 'Received orders have been delivered and verified. Inventory has been updated.'}
            {statusFilter === 'cancelled' && 'Cancelled orders were voided before completion. No inventory changes were applied.'}
          </span>
        </div>
      )}
      <div className="filters">
        <select value={locFilter} onChange={e => setLocFilter(e.target.value)}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={supFilter} onChange={e => setSupFilter(e.target.value)}>
          <option value="">All Suppliers</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="search-input">
          <Search size={16} />
          <input placeholder="Search PO #, location, supplier..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--faint)' }}>{filtered.length} order{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr>
            {thSort('PO #', 'po')}
            {thSort('Location', 'location')}
            {thSort('Supplier', 'supplier')}
            {thSort('Status', 'status')}
            {thSort('Total', 'total')}
            {thSort('Created', 'created')}
            <th>Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id}>
                <td style={{ fontWeight: 600, color: 'var(--gold)' }}>{o.po_number}</td>
                <td>{o.location_name}</td>
                <td>{o.supplier_name}</td>
                <td><StatusPill status={o.status} />{o.needs_approval ? <span style={{ marginLeft: 4, fontSize: 10, padding: '2px 6px', borderRadius: 8, background: 'rgba(243,156,18,.15)', color: 'var(--warning)', fontWeight: 600 }}>NEEDS APPROVAL</span> : null}</td>
                <td style={{ fontWeight: 600 }}>{showCosts ? money(o.total_amount) : '•••'}</td>
                <td style={{ color: 'var(--faint)' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => viewOrder(o.id)}><Eye size={14} /> View</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && <p style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>No orders found</p>}

      {/* PO Detail Modal */}
      {detail && (
        <Modal title={'PO ' + detail.po_number} onClose={() => setDetail(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Location</span><div style={{ fontWeight: 500, marginTop: 2 }}>{detail.location_name}</div></div>
            <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Supplier</span><div style={{ fontWeight: 500, marginTop: 2 }}>{detail.supplier_name}</div></div>
            <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Status</span><div style={{ marginTop: 4 }}><StatusPill status={detail.status} /></div></div>
            <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Total</span><div style={{ fontWeight: 700, marginTop: 2, color: 'var(--gold)', fontSize: 16 }}>{showCosts ? money(detail.total_amount) : '•••'}</div></div>
            <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Created By</span><div style={{ marginTop: 2 }}>{detail.created_by_name}</div></div>
            <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Created</span><div style={{ marginTop: 2 }}>{new Date(detail.created_at).toLocaleDateString()}</div></div>
            {detail.cancelled_by_name && <div><span style={{ fontSize: 12, color: 'var(--danger)' }}>Cancelled By</span><div style={{ marginTop: 2, color: 'var(--danger)' }}>{detail.cancelled_by_name} — {detail.cancelled_at ? new Date(detail.cancelled_at).toLocaleDateString() : ''}</div></div>}
            {detail.received_by_name && <div><span style={{ fontSize: 12, color: 'var(--success)' }}>Received By</span><div style={{ marginTop: 2, color: 'var(--success)' }}>{detail.received_by_name} — {detail.received_at ? new Date(detail.received_at).toLocaleDateString() : ''}</div></div>}
            {detail.needs_approval && (
              <div style={{ gridColumn: '1 / -1', padding: '10px 14px', background: 'rgba(243,156,18,.1)', border: '1px solid rgba(243,156,18,.2)', borderRadius: 8, fontSize: 13, color: 'var(--warning)' }}>
                This PO requires management approval before it can be sent.
                {detail.approved_by_name && <span> — Approved by {detail.approved_by_name}</span>}
              </div>
            )}
          </div>
          <div className="table-wrap" style={{ marginBottom: 16 }}>
            <table>
              <thead><tr><th>Product</th><th>Ordered</th>{detail.status === 'received' && <th>Received</th>}{['sent','confirmed'].includes(detail.status) && <th>Receive Qty</th>}<th>Cost</th><th>Total</th></tr></thead>
              <tbody>
                {(detail.items || []).map((item, idx) => (
                  <tr key={item.id}>
                    <td><div style={{ fontWeight: 600, cursor:"pointer", borderBottom:"1px dashed var(--faint)", display:"inline" }} onClick={() => setViewProductId(item.product_id)} onMouseEnter={e => e.currentTarget.style.color="var(--gold)"} onMouseLeave={e => e.currentTarget.style.color="var(--text)"}>{item.product_name}</div><div style={{ fontSize: 11, color: 'var(--faint)' }}>{item.sku}</div></td>
                    <td>{Number(item.qty_ordered).toFixed(0)}</td>
                    {detail.status === 'received' && <td style={{ color: 'var(--success)', fontWeight: 600 }}>{Number(item.qty_received).toFixed(0)}</td>}
                    {['sent','confirmed'].includes(detail.status) && (
                      <td><input type="number" value={receiveItems[idx]?.qty_to_receive ?? ''} min="0" onChange={e => { const n = [...receiveItems]; n[idx] = { ...n[idx], qty_to_receive: parseFloat(e.target.value) || 0 }; setReceiveItems(n); }} style={{ width: 70, textAlign: 'center' }} /></td>
                    )}
                    <td>{showCosts ? money(item.unit_cost) : '•••'}</td>
                    <td style={{ fontWeight: 600 }}>{showCosts ? money(item.line_total) : '•••'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {detail.notes && <div style={{ padding: 12, background: 'var(--navy)', borderRadius: 8, fontSize: 13, color: 'var(--muted)', marginBottom: 16, whiteSpace: 'pre-wrap' }}>{detail.notes}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {detail.status === 'sent' && detail.supplier_portal_token && (
              <button className="btn btn-secondary" onClick={() => copyPortalLink(detail.supplier_portal_token)}><Copy size={14} /> Copy Supplier Portal Link</button>
            )}
            {canApprove && detail.needs_approval && (
              <>
                <button className="btn btn-primary" onClick={() => approvePO(detail.id)}>Approve PO</button>
                <button className="btn btn-danger" onClick={() => rejectPO(detail.id)}>Reject</button>
              </>
            )}
            {canSend && detail.status === 'draft' && !detail.needs_approval && (
              <button className="btn btn-primary" onClick={() => sendOrder(detail.id)}><Send size={14} /> Send to Supplier</button>
            )}
            {canReceive && ['sent', 'confirmed'].includes(detail.status) && (
              <button className="btn btn-primary" onClick={() => receiveOrder(detail.id)}><PackageCheck size={14} /> Confirm Receipt</button>
            )}
            {canCancel && detail.status !== 'cancelled' && (
              <button className="btn btn-danger" onClick={() => cancelOrder(detail.id)}><X size={14} /> {detail.status === 'received' ? 'Cancel / Reverse' : 'Cancel PO'}</button>
            )}
          </div>
        </Modal>
      )}

      {/* Create PO Modal */}
      {showCreate && (
        <Modal title="Create Purchase Order" onClose={() => setShowCreate(false)}>
          <div className="grid-2">
            <div className="form-group"><label>Location</label>
              <select value={createForm.location_id} onChange={e => setCreateForm({...createForm, location_id: e.target.value})} required>
                <option value="">Select...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Supplier</label>
              <select value={createForm.supplier_id} onChange={e => setCreateForm({...createForm, supplier_id: e.target.value})} required>
                <option value="">Select...</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <h4 style={{ margin: '16px 0 8px', color: 'var(--gold)' }}>Line Items</h4>
          {lineItems.map((li, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
              <select value={li.product_id} onChange={e => { const n = [...lineItems]; n[idx].product_id = e.target.value; const prod = products.find(p => p.id == e.target.value); if (prod) n[idx].unit_cost = prod.unit_cost; setLineItems(n); }}>
                <option value="">Select product...</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input type="number" placeholder="Qty" value={li.qty_ordered} onChange={e => { const n = [...lineItems]; n[idx].qty_ordered = e.target.value; setLineItems(n); }} min="1" />
              <input type="number" placeholder="Cost" value={li.unit_cost} onChange={e => { const n = [...lineItems]; n[idx].unit_cost = e.target.value; setLineItems(n); }} min="0" step="0.01" />
            </div>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={addLineItem} style={{ marginTop: 8 }}><Plus size={14} /> Add Item</button>
          {lineItems.length > 0 && (
            <div style={{ textAlign: 'right', marginTop: 12, fontWeight: 700, color: 'var(--gold)' }}>
              Total: {showCosts ? money(lineItems.reduce((s, li) => s + (parseFloat(li.qty_ordered) || 0) * (parseFloat(li.unit_cost) || 0), 0)) : '•••'}
            </div>
          )}
          <div className="form-group" style={{ marginTop: 16 }}><label>Notes</label><textarea value={createForm.notes} onChange={e => setCreateForm({...createForm, notes: e.target.value})} rows={2} /></div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn btn-secondary" onClick={() => submitCreate(false)} style={{ fontWeight: 600 }}>Save as Draft</button>
            <button className="btn btn-primary" onClick={() => submitCreate(true)}><Send size={14} /> Create & Send</button>
          </div>
        </Modal>
      )}
      {viewProductId && <ProductDetail productId={viewProductId} onClose={() => setViewProductId(null)} />}
    </div>
  );
}
